import { useDebounce } from 'hooks/useDebounce'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { Alert, Button, CircularProgress } from '@mui/material'
import { UserLayout } from 'components/layout/UserLayout'
import { CreativeCard } from '../creatives/CreativeCard/CreativeCard'
import { CreativeCardSkeleton } from './CreativeCard/CreativeCardSkeleton'
import { NewCreativeModal } from './components/NewCreativeModal/NewCreativeModal'
import {
  CreativeEditorModal,
  useOpenAd,
} from './components/CreativeEditorModal/CreativeEditorModal'
import { BulkShareCreatives } from './components/bulk-operations/BulkShareCreatives'
import { CreativeImportButton } from './creative-import/CreativeImportButton'
import { CreativeFiltersPanel } from './components/CreativeFiltersPanel'
import { ListPageBar, PageBarFilters } from 'components/layout/PageBar'
import { FullPageContent } from 'components/layout/FullPageLayout/Content/FullPageContent'
import { sessionContext, useGetBusinessID } from '@marketing-milk/frontend'
import { Permissions } from '@marketing-milk/interfaces'
import { useCreatives } from './useCreatives'
import { NoItemsCreated } from 'components/NoItemsCreated/NoItemsCreated'
import { useRefreshVideoAssets } from 'hooks/creatives/useRefreshVideoAssets'
import { isEmpty, range } from 'lodash'

const DEFAULT_SORT_PROP = 'lastUsed'
const DEFAULT_SORT_DIRECTION = 'DESC'

export const Creatives = () => {
  const businessID = useGetBusinessID()
  const [searchTerm, setSearchInput] = useState('')
  const { debouncedValue: debouncedSearch } = useDebounce(searchTerm)
  const {
    creatives,
    count,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isFetching,
    isPreviousData,
    sortProp,
    setSortProp,
    setSortDirection,
    sortDirection,
    setSearchTerm,
    fetchNextPage,
    filterValues,
    setFilterValues,
    refetch,
  } = useCreatives(businessID, { take: 50, sortProp: DEFAULT_SORT_PROP })

  // A creative whose video relation went missing renders as broken media on its
  // card, and this is the page where that is seen — so the re-sync that repairs
  // it is reachable from here rather than only from a sprint packet. It syncs the
  // whole business either way; the packet's copy is not sprint-scoped either.
  const { user } = useContext(sessionContext)
  const isStaff = !!user?.permissions.includes(Permissions.AccessAdminApp)
  const { refresh, isRefreshing } = useRefreshVideoAssets(Number(businessID), {
    refetchCreatives: refetch,
    showAuthorizerLink: isStaff,
  })
  useEffect(() => {
    setSearchTerm(debouncedSearch)
  }, [debouncedSearch, setSearchTerm])

  const creativeListRef = useRef<HTMLDivElement>(null)
  const { open: openAd } = useOpenAd()

  // The badge counts the sort too — it is now hidden behind the control, so a
  // non-default sort is otherwise invisible.
  const activeFilterCount =
    (isEmpty(filterValues?.type) ? 0 : 1) +
    (sortProp !== DEFAULT_SORT_PROP || sortDirection !== DEFAULT_SORT_DIRECTION ? 1 : 0)

  const resetView = () => {
    setSortProp(DEFAULT_SORT_PROP)
    setSortDirection(DEFAULT_SORT_DIRECTION)
    setFilterValues(undefined)
  }

  // A narrowed view that came back empty is a different situation from a business
  // with no creatives, and offering "Add Creative" to someone who mistyped a search
  // answers a question they didn't ask.
  const isNarrowed = Boolean(searchTerm) || !isEmpty(filterValues?.type)
  const hasNoResults = isEmpty(creatives) && !isFetching && !isError
  // The query keeps the previous page's results on screen while the next query
  // runs, so an empty list plus a fetch in flight is the first load and nothing else.
  const isFirstLoad = isEmpty(creatives) && isFetching && !isFetchingNextPage

  return (
    <UserLayout>
      <ListPageBar
        subject="Creatives"
        search={{ value: searchTerm, onChange: setSearchInput, placeholder: 'Search creatives' }}
        filters={
          <PageBarFilters activeCount={activeFilterCount} onClear={resetView}>
            <CreativeFiltersPanel
              sortProp={sortProp}
              setSort={setSortProp}
              sortDirection={sortDirection}
              setSortDirection={setSortDirection}
              filters={filterValues}
              setFilters={setFilterValues}
            />
          </PageBarFilters>
        }
        actions={
          <>
            <CreativeImportButton />
            <NewCreativeModal>
              <Button variant="outlined" color="primary">
                Add creative
              </Button>
            </NewCreativeModal>
            <BulkShareCreatives />
            {isStaff && (
              <Button variant="text" size="small" onClick={refresh} disabled={isRefreshing}>
                <i
                  className={`fa fa-${isRefreshing ? 'spinner fa-spin' : 'refresh'} mr-2`}
                  aria-hidden
                />
                {isRefreshing ? 'Refreshing video assets…' : 'Refresh video assets'}
              </Button>
            )}
          </>
        }
        status={
          <span role="status">
            {isPreviousData || isFirstLoad
              ? 'Loading creatives…'
              : isError
              ? 'Could not update creatives'
              : `Showing ${(
                  creatives?.length ?? 0
                ).toLocaleString()} of ${count.toLocaleString()} creatives`}
          </span>
        }
      />

      <FullPageContent
        ref={creativeListRef}
        className="bg-mm-neutral-1-50 p-4 sm:p-6"
        onScroll={() => {
          if (isFetchingNextPage || isPreviousData || isError || !hasNextPage) return
          if (!creativeListRef.current) return
          const { scrollHeight, scrollTop, clientHeight } = creativeListRef.current
          const currentPosition = scrollTop + clientHeight
          const positionToReach = scrollHeight - 100
          if (currentPosition > positionToReach) fetchNextPage()
        }}
      >
        {isError && (
          <Alert
            severity="error"
            className="mb-4"
            action={
              <Button color="inherit" size="small" onClick={() => refetch()} disabled={isFetching}>
                {isFetching ? 'Retrying…' : 'Try again'}
              </Button>
            }
          >
            {isEmpty(creatives)
              ? 'We couldn’t load your creatives. Please try again.'
              : 'We couldn’t update your creatives. The previously loaded results are still shown.'}
          </Alert>
        )}
        <div
          aria-busy={isPreviousData || isFirstLoad}
          // A search or filter dims the results it is about to replace, so a change
          // that alters nothing visible still reads as having been heard. Keyed off
          // stale-previous-results rather than any fetch, so the refresh that runs
          // when the tab regains focus leaves the grid alone.
          className={`grid w-full grid-cols-[repeat(auto-fill,minmax(min(100%,20rem),1fr))] gap-4 transition-opacity ${
            isPreviousData && !isFirstLoad ? 'opacity-40' : ''
          }`}
        >
          {isFirstLoad
            ? range(0, 12).map(i => <CreativeCardSkeleton key={i} />)
            : (creatives ?? []).map(creative => (
                <CreativeCard
                  key={creative.id}
                  creative={creative}
                  onSelect={() => openAd(creative.id)}
                  pillDisplayed={'format'}
                  propertiesDisplayed={['name']}
                />
              ))}
        </div>

        {isFetchingNextPage ? (
          <div className="flex w-full justify-center py-6">
            <CircularProgress color="primary" size={28} aria-label="Loading more creatives" />
          </div>
        ) : hasNextPage && !isError ? (
          <div className="flex w-full justify-center py-6">
            <Button variant="outlined" onClick={() => fetchNextPage()} disabled={isFetching}>
              Load more creatives
            </Button>
          </div>
        ) : null}

        {hasNoResults && (
          <div className="flex w-full justify-center">
            {isNarrowed ? (
              <NoItemsCreated
                title="No creatives match this search"
                subtitle={
                  searchTerm
                    ? `Nothing here is named like “${searchTerm}”.`
                    : 'Nothing here matches the filters you have set.'
                }
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    setSearchInput('')
                    resetView()
                  }}
                >
                  Clear search and filters
                </Button>
              </NoItemsCreated>
            ) : (
              <NoItemsCreated
                title="No creatives yet"
                subtitle="Import ads from an existing account, or build one here."
              >
                <NewCreativeModal>
                  <Button variant="contained" color="primary">
                    Add creative
                  </Button>
                </NewCreativeModal>
              </NoItemsCreated>
            )}
          </div>
        )}
      </FullPageContent>

      <CreativeEditorModal
        creatives={creatives ?? []}
        total={count}
        loadMore={
          hasNextPage
            ? async () => (await fetchNextPage()).data?.pages.flatMap(page => page.creatives)
            : undefined
        }
      />
    </UserLayout>
  )
}

export default Creatives
